import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/logo";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Sparkles, AlertTriangle, ShieldAlert, ChevronLeft, GraduationCap } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { detectCountry } from "@/lib/jurisdiction.functions";
import { submitSuitability } from "@/lib/suitability.functions";
import { getCompliancePublicConfig } from "@/lib/compliance/config.functions";
import {
  COUNTRY_LIST,
  DEFAULT_JURISDICTIONS_CONFIG,
  classifyCountry,
  type JurisdictionsConfig,
} from "@/lib/compliance/constants";
import { FullDisclosureModal } from "@/components/full-disclosure-modal";

export const Route = createFileRoute("/onboarding")({
  ssr: false,
  component: OnboardingPage,
});

const PLANS = [
  { id: "free", name: "Free", price: "$0", desc: "3 pairs · delayed intelligence" },
  { id: "pro", name: "Pro", price: "$29", desc: "All 8 pairs · real-time" },
  { id: "elite", name: "Elite", price: "$79", desc: "Everything + API access" },
] as const;

type Experience = "never" | "lt1" | "1to3" | "3plus";
type Step = 1 | 2 | 3 | 4 | 5;

function OnboardingPage() {
  const navigate = useNavigate();
  const detect = useServerFn(detectCountry);
  const saveSuitability = useServerFn(submitSuitability);
  const loadCompliance = useServerFn(getCompliancePublicConfig);

  const [email, setEmail] = useState<string | null>(null);
  const [step, setStep] = useState<Step>(1);

  // Step state
  const [experience, setExperience] = useState<Experience | null>(null);
  const [risksOk, setRisksOk] = useState<boolean | null>(null);
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);
  const [country, setCountry] = useState<string>("");
  const [jurisdictionConfig, setJurisdictionConfig] = useState<JurisdictionsConfig>(
    DEFAULT_JURISDICTIONS_CONFIG,
  );
  const [jurisdictionAcknowledged, setJurisdictionAcknowledged] = useState(false);
  const [usAcknowledged, setUsAcknowledged] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [disclosureOpen, setDisclosureOpen] = useState(false);

  // Plan + terms
  const [plan, setPlan] = useState<"free" | "pro" | "elite">("free");
  const [terms, setTerms] = useState(false);

  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Session + bootstrap
  useEffect(() => {
    let active = true;
    void (async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase.auth.getSession();
      if (!active) return;
      if (!data.session) {
        navigate({ to: "/login", replace: true });
        return;
      }
      const userEmail = data.session.user.email ?? null;
      setEmail(userEmail);
      if ((userEmail ?? "").toLowerCase() === "ahmity@gmail.com") {
        navigate({ to: "/dashboard", replace: true });
        return;
      }
      try {
        const { getOnboardingStatus } = await import("@/lib/user.functions");
        const status = await getOnboardingStatus();
        if (!active) return;
        if (!status.needsOnboarding) {
          localStorage.setItem(`forexmind:onboarded:${data.session.user.id}`, "true");
          navigate({ to: "/dashboard", replace: true });
        }
      } catch {
        // proceed
      }

      try {
        const cfg = await loadCompliance();
        if (active) setJurisdictionConfig(cfg.jurisdictions);
      } catch {
        // keep defaults
      }

      try {
        const { country: cc } = await detect();
        if (!active) return;
        if (cc) {
          setDetectedCountry(cc);
          setCountry(cc);
        }
      } catch {
        // ignore
      }
    })();
    return () => {
      active = false;
    };
  }, [navigate, detect, loadCompliance]);

  const goNext = () => setStep((s) => (Math.min(5, s + 1) as Step));
  const goBack = () => setStep((s) => (Math.max(1, s - 1) as Step));

  const classification = classifyCountry(country, jurisdictionConfig);
  const showJurisdictionWarning = classification === "warning";
  const isBlocked = classification === "blocked";
  const isUS = country === "US";
  const isWeakExperience = experience === "never" || experience === "lt1";

  const submitFinal = async () => {
    setErr(null);
    if (!experience) return setErr("Please choose your experience level.");
    if (risksOk !== true) return setErr("You must acknowledge the risks to continue.");
    if (!country || !/^[A-Z]{2}$/.test(country))
      return setErr("Please select your country of residence.");
    if (isBlocked)
      return setErr("Access from your jurisdiction is restricted by platform policy.");
    if (showJurisdictionWarning && !jurisdictionAcknowledged)
      return setErr("Please acknowledge the jurisdiction notice.");
    if (isUS && !usAcknowledged)
      return setErr("US residents must acknowledge the regulatory notice.");
    if (!disclaimerAccepted) return setErr("Please accept the full risk disclosure.");
    if (!terms) return setErr("Please accept the Terms to continue.");
    setLoading(true);
    try {
      await saveSuitability({
        data: {
          experience,
          risksAcknowledged: true,
          countrySelected: country as string,
          jurisdictionWarningShown: showJurisdictionWarning,
          jurisdictionAcknowledged: showJurisdictionWarning ? jurisdictionAcknowledged : false,
          usAcknowledged: isUS ? usAcknowledged : false,
          disclaimerAccepted: true,
          termsAccepted: true,
        },
      });
      const { completeOAuthOnboarding } = await import("@/lib/user.functions");
      await completeOAuthOnboarding({ data: { plan, terms_agreed: true } });
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase.auth.getSession();
      if (data.session?.user.id) {
        localStorage.setItem(`forexmind:onboarded:${data.session.user.id}`, "true");
        localStorage.setItem(
          `forexmind:onboarded:checked-at:${data.session.user.id}`,
          String(Date.now()),
        );
      }
      toast.success("Welcome aboard");
      navigate({ to: plan === "free" ? "/dashboard" : "/upgrade", replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative grid min-h-screen place-items-center overflow-hidden bg-grid px-4 py-10"
      style={{ background: "var(--gradient-hero)" }}
    >
      <div className="absolute inset-0" style={{ background: "var(--gradient-glow)" }} />
      <div className="relative w-full max-w-lg">
        <div className="mb-6 flex justify-center">
          <Logo size="lg" variant="stacked" />
        </div>
        <Card className="border-border/60 bg-card/80 p-7 backdrop-blur shadow-[var(--shadow-card)]">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-primary">
              <Sparkles className="h-5 w-5" />
              <span className="text-xs uppercase tracking-widest">Welcome to ForexMind AI</span>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Step {step} / 5
            </span>
          </div>

          {email && <p className="mb-4 text-xs text-muted-foreground">Signed in as {email}.</p>}

          {step === 1 && (
            <StepWrap
              title="Trading experience"
              subtitle="How long have you been trading forex or CFDs?"
            >
              <div className="space-y-2">
                {[
                  { id: "never", label: "I have never traded before" },
                  { id: "lt1", label: "Less than 1 year" },
                  { id: "1to3", label: "1 – 3 years" },
                  { id: "3plus", label: "3+ years" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setExperience(opt.id as Experience)}
                    className={cn(
                      "w-full rounded-lg border p-3 text-left text-sm transition-colors",
                      experience === opt.id
                        ? "border-primary bg-primary/10"
                        : "border-border/60 bg-card/40 hover:border-primary/40",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {isWeakExperience && (
                <div className="mt-4 rounded-md border border-warn/40 bg-warn/10 p-3 text-[12px] leading-snug text-warn">
                  <div className="mb-1 flex items-center gap-1.5 font-semibold">
                    <GraduationCap className="h-3.5 w-3.5" /> Strong educational warning
                  </div>
                  <p>
                    Forex and CFD trading is high-risk and not designed for beginners. Most
                    new retail traders lose money. Before continuing we strongly recommend
                    reviewing our{" "}
                    <Link to="/risk-education" className="underline">
                      educational resources
                    </Link>
                    .
                  </p>
                </div>
              )}

              <Footer
                disabled={!experience}
                onNext={goNext}
                nextLabel="Continue"
                showBack={false}
              />
            </StepWrap>
          )}

          {step === 2 && (
            <StepWrap
              title="Risk understanding"
              subtitle="Do you understand that forex and CFD trading can result in significant loss, including total loss of trading capital?"
            >
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    setRisksOk(true);
                    goNext();
                  }}
                  className={cn(
                    "w-full rounded-lg border p-3 text-left text-sm transition-colors",
                    risksOk === true
                      ? "border-primary bg-primary/10"
                      : "border-border/60 bg-card/40 hover:border-primary/40",
                  )}
                >
                  Yes, I understand the risks
                </button>
                <button
                  type="button"
                  onClick={() => setRisksOk(false)}
                  className={cn(
                    "w-full rounded-lg border p-3 text-left text-sm transition-colors",
                    risksOk === false
                      ? "border-sell/60 bg-sell/10"
                      : "border-border/60 bg-card/40 hover:border-sell/40",
                  )}
                >
                  No, I do not understand
                </button>
              </div>

              {risksOk === false && (
                <div className="mt-4 rounded-md border border-sell/40 bg-sell/10 p-3 text-xs text-sell">
                  <p className="mb-2">
                    Market intelligence is not unlocked until you understand the risks
                    involved. Please review the educational resources first.
                  </p>
                  <Button
                    asChild
                    size="sm"
                    variant="outline"
                    className="border-sell/40 text-sell hover:bg-sell/10"
                  >
                    <Link to="/risk-education">Open risk education →</Link>
                  </Button>
                </div>
              )}

              <Footer
                disabled={risksOk !== true}
                onNext={goNext}
                onBack={goBack}
                nextLabel="Continue"
              />
            </StepWrap>
          )}

          {step === 3 && (
            <StepWrap
              title="Jurisdiction check"
              subtitle="What country are you currently resident in?"
            >
              {detectedCountry && (
                <p className="mb-3 text-[11px] text-muted-foreground">
                  Detected from your connection: <span className="font-mono">{detectedCountry}</span>.
                  Please confirm or correct below.
                </p>
              )}
              <select
                value={country}
                onChange={(e) => {
                  setCountry(e.target.value);
                  setJurisdictionAcknowledged(false);
                  setUsAcknowledged(false);
                }}
                className="w-full rounded-md border border-border/60 bg-card/40 px-3 py-2 text-sm"
              >
                <option value="">Select your country…</option>
                {COUNTRY_LIST.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>

              {isBlocked && (
                <div className="mt-4 rounded-md border border-sell/40 bg-sell/10 p-3 text-[12px] leading-snug text-sell">
                  <div className="mb-1 flex items-center gap-1.5 font-semibold">
                    <ShieldAlert className="h-3.5 w-3.5" /> Access restricted
                  </div>
                  <p>
                    Based on platform policy, access from your jurisdiction is currently
                    restricted. If you believe this is in error, please contact{" "}
                    <a className="underline" href="mailto:legal@forexmind.ai">
                      legal@forexmind.ai
                    </a>
                    .
                  </p>
                </div>
              )}

              {showJurisdictionWarning && !isBlocked && (
                <div className="mt-4 rounded-md border border-warn/40 bg-warn/10 p-3 text-[12px] leading-snug text-warn">
                  <div className="mb-1 flex items-center gap-1.5 font-semibold">
                    <ShieldAlert className="h-3.5 w-3.5" /> Regulatory notice
                  </div>
                  <p>
                    Forex and CFD-related market commentary, signals, and financial promotions
                    may be restricted or regulated in your jurisdiction. ForexMind AI does not
                    provide regulated financial advice, brokerage services, trade execution, or
                    account management. Access may be limited based on local laws and platform
                    policy.
                  </p>
                  <label className="mt-3 flex items-start gap-2 text-[12px]">
                    <input
                      type="checkbox"
                      checked={jurisdictionAcknowledged}
                      onChange={(e) => setJurisdictionAcknowledged(e.target.checked)}
                      className="mt-0.5 h-3.5 w-3.5 accent-warn"
                    />
                    <span>I acknowledge this notice and confirm I am proceeding for educational purposes only.</span>
                  </label>
                </div>
              )}

              {isUS && !isBlocked && (
                <div className="mt-3 rounded-md border border-warn/40 bg-warn/10 p-3 text-[12px] leading-snug text-warn">
                  <p>
                    Retail off-exchange forex in the US is regulated by the CFTC/NFA.
                    ForexMind AI is not a registered Commodity Trading Advisor and does not
                    provide individualized trading advice.
                  </p>
                  <label className="mt-3 flex items-start gap-2 text-[12px]">
                    <input
                      type="checkbox"
                      checked={usAcknowledged}
                      onChange={(e) => setUsAcknowledged(e.target.checked)}
                      className="mt-0.5 h-3.5 w-3.5 accent-warn"
                    />
                    <span>I acknowledge the US regulatory notice.</span>
                  </label>
                </div>
              )}

              <Footer
                disabled={
                  !country ||
                  isBlocked ||
                  (showJurisdictionWarning && !jurisdictionAcknowledged) ||
                  (isUS && !usAcknowledged)
                }
                onNext={goNext}
                onBack={goBack}
                nextLabel="Continue"
              />
            </StepWrap>
          )}

          {step === 4 && (
            <StepWrap title="Risk disclosure" subtitle="Please read and accept the full risk disclosure.">
              <div className="max-h-56 overflow-y-auto rounded-md border border-warn/30 bg-warn/5 p-3 text-[11.5px] leading-relaxed text-muted-foreground">
                <p className="mb-2 flex items-center gap-1.5 font-semibold text-warn">
                  <AlertTriangle className="h-3.5 w-3.5" /> Risk disclosure summary
                </p>
                <p className="mb-2">
                  ForexMind AI provides AI-powered market intelligence for educational
                  purposes only. <strong>This is not financial advice.</strong>
                </p>
                <p className="mb-2">
                  Forex and CFD trading involves significant risk of loss and may not be
                  suitable for all investors. Leverage can amplify losses as quickly as gains.
                  Past performance does not guarantee future results.
                </p>
                <p>
                  Only trade with capital you can afford to lose. The platform is not available
                  to residents of certain jurisdictions.
                </p>
              </div>

              <div className="mt-3 flex items-center justify-between text-[11px]">
                <button
                  type="button"
                  onClick={() => setDisclosureOpen(true)}
                  className="underline text-primary hover:text-primary/80"
                >
                  Read full risk disclosure
                </button>
                <Link to="/risk-disclosure" target="_blank" className="text-muted-foreground hover:text-foreground underline">
                  Open in new tab
                </Link>
              </div>

              <label className="mt-3 flex items-start gap-2 text-[12px] text-muted-foreground">
                <input
                  type="checkbox"
                  checked={disclaimerAccepted}
                  onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 accent-primary"
                />
                <span>
                  I confirm that I have read and understood the risk disclosure. I understand
                  that ForexMind AI provides educational market intelligence only and does not
                  provide financial advice, investment advice, brokerage services, trade
                  execution, or account management.
                </span>
              </label>

              <Footer
                disabled={!disclaimerAccepted}
                onNext={goNext}
                onBack={goBack}
                nextLabel="Continue"
              />
              <FullDisclosureModal open={disclosureOpen} onOpenChange={setDisclosureOpen} />
            </StepWrap>
          )}

          {step === 5 && (
            <StepWrap title="Choose your plan" subtitle="Pick the plan that fits your needs.">
              <div className="grid grid-cols-3 gap-2">
                {PLANS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPlan(p.id)}
                    className={cn(
                      "rounded-lg border p-3 text-left transition-colors",
                      plan === p.id
                        ? "border-primary bg-primary/10"
                        : "border-border/60 bg-card/40 hover:border-primary/40",
                    )}
                  >
                    <div className="flex items-baseline justify-between">
                      <span className="font-display text-sm font-bold">{p.name}</span>
                      <span className="font-mono text-xs text-primary">{p.price}</span>
                    </div>
                    <p className="mt-1 text-[10px] leading-tight text-muted-foreground">{p.desc}</p>
                  </button>
                ))}
              </div>

              <label className="mt-4 flex items-start gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={terms}
                  onChange={(e) => setTerms(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 accent-primary"
                />
                <span>
                  I agree to the{" "}
                  <Link to="/terms" className="underline hover:text-foreground">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="underline hover:text-foreground">
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>

              {err && (
                <div className="mt-3 rounded-md border border-sell/40 bg-sell/10 px-3 py-2 text-xs text-sell">
                  {err}
                </div>
              )}

              <div className="mt-5 flex items-center justify-between gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={goBack}
                  className="text-xs text-muted-foreground"
                >
                  <ChevronLeft className="mr-1 h-3 w-3" /> Back
                </Button>
                <Button
                  type="button"
                  onClick={submitFinal}
                  disabled={loading || !terms}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {loading ? "Setting up…" : "Start exploring →"}
                </Button>
              </div>
            </StepWrap>
          )}
        </Card>
      </div>
    </div>
  );
}

function StepWrap({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-foreground">{title}</h1>
      <p className="mt-1 mb-4 text-sm text-muted-foreground">{subtitle}</p>
      {children}
    </div>
  );
}

function Footer({
  disabled,
  onNext,
  onBack,
  nextLabel,
  showBack = true,
}: {
  disabled?: boolean;
  onNext: () => void;
  onBack?: () => void;
  nextLabel: string;
  showBack?: boolean;
}) {
  return (
    <div className="mt-5 flex items-center justify-between gap-2">
      {showBack && onBack ? (
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          className="text-xs text-muted-foreground"
        >
          <ChevronLeft className="mr-1 h-3 w-3" /> Back
        </Button>
      ) : (
        <span />
      )}
      <Button
        type="button"
        onClick={onNext}
        disabled={disabled}
        className="bg-primary text-primary-foreground hover:bg-primary/90"
      >
        {nextLabel} →
      </Button>
    </div>
  );
}
